import { FormControl, FormField } from '@components/ui/form';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/form/select';
import { ContactsDropdown } from '@services/dropdowns/companies/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropdownsContext } from '@contexts/dropdowns';
import { ExternalScheduleContactsInputsProps } from './types';

export const ExternalScheduleContactsInputs = ({
  form: { control, setValue, watch, resetField },
  jobId,
}: ExternalScheduleContactsInputsProps) => {
  const {
    companies: { options, disabled },
    setParams,
  } = useDropdownsContext();

  const { t } = useTranslation('app');
  const [categoryId, setCategoryId] = useState('');
  const [contacts, setContacts] = useState<ContactsDropdown[]>([]);
  const externalContactId = watch('externalContactId');

  useEffect(() => {
    if (!jobId) return;
    setParams({ companies: { jobId } });
  }, [jobId, setParams]);

  useEffect(() => {
    if (categoryId === 'job') setContacts(options.jobContacts || []);
    else setContacts(options.companies.find((el) => el.id === categoryId)?.contacts || []);
  }, [categoryId, setContacts, options]);

  useEffect(() => {
    const newCategoryId =
      options.companies.find((el) =>
        el.contacts.find((contact) => contact.id === externalContactId),
      )?.id || 'job';
    if (newCategoryId) setCategoryId(newCategoryId);
  }, [options, externalContactId, setValue]);

  return (
    <div className="flex w-full flex-1 gap-4">
      <FormItemWrapper label={t('forms.category.label')} select required>
        <Select
          disabled={disabled}
          value={categoryId}
          onValueChange={(v) => {
            resetField('externalContactId');
            setCategoryId(v);
          }}>
          <FormControl>
            <SelectTrigger disabled={disabled}>
              <SelectValue placeholder={t('forms.default.select.placeholder')} />
            </SelectTrigger>
          </FormControl>
          <SelectContent>
            <SelectItem value="job">{t('schedules.contacts.job')}</SelectItem>
            {!disabled && (
              <>
                <p className="cursor-not-allowed select-none px-2 py-1.5 text-sm">
                  {t('schedules.contacts.external')}
                </p>
                {options.companies.map(({ id, name }) => (
                  <SelectItem className="pl-4" key={id} value={id}>
                    {name}
                  </SelectItem>
                ))}
              </>
            )}
          </SelectContent>
        </Select>
      </FormItemWrapper>
      <FormField
        control={control}
        name="externalContactId"
        render={({ field: { value, onChange } }) => (
          <FormItemWrapper label={t('forms.name.label')} select required>
            <Select value={value} onValueChange={onChange} disabled={!categoryId}>
              <FormControl>
                <SelectTrigger name="externalContactId" disabled={!categoryId}>
                  <SelectValue
                    id="externalContactId"
                    placeholder={t('forms.default.select.placeholder')}
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {contacts?.map(({ id, name }) => (
                  <SelectItem key={id} value={id || ''}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItemWrapper>
        )}
      />
    </div>
  );
};
